import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { FaComments, FaPaperPlane, FaTimes, FaUser } from "react-icons/fa";

const ChatWidget = ({ user }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen]);

    useEffect(() => {
        if (!user || !isOpen) return;

        const fetchMessages = async () => {
            const { data, error } = await supabase
                .from("messages")
                .select("*")
                .eq("user_id", user.id)
                .order("created_at", { ascending: true });

            if (error) console.error("Error fetching messages:", error);
            else setMessages(data || []);
        };

        fetchMessages();

        const channel = supabase
            .channel("public:messages")
            .on(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "messages",
                    filter: `user_id=eq.${user.id}`,
                },
                (payload) => {
                    setMessages((prev) => [...prev, payload.new]);
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user, isOpen]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !user) return;

        const messageToSend = newMessage.trim();
        setNewMessage(""); // Clear input segera agar responsif

        const { error } = await supabase.from("messages").insert([
            {
                user_id: user.id,
                sender: "user",
                content: messageToSend,
            },
        ]);

        if (error) {
            console.error("Error sending message:", error);
        }
    };

    return (
        <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3 pointer-events-none">
            {isOpen && (
                <div className="pointer-events-auto flex h-[28rem] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-pop animate-scale-in dark:border-slate-800 dark:bg-slate-900">
                    {/* Header */}
                    <div className="flex items-center justify-between bg-gradient-to-r from-primary-600 to-secondary-600 px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white">
                                <FaUser className="h-3.5 w-3.5" />
                            </span>
                            <div>
                                <p className="text-sm font-semibold text-white">Admin Support</p>
                                <p className="flex items-center gap-1 text-[11px] text-white/80">
                                    <span className="h-1.5 w-1.5 rounded-full bg-success-300" /> Online
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
                            aria-label="Tutup chat"
                        >
                            <FaTimes className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4 scrollbar-slim dark:bg-slate-950/40">
                        {messages.length === 0 && (
                            <div className="flex h-full flex-col items-center justify-center text-center text-slate-400">
                                <FaComments className="mb-2 h-8 w-8 opacity-30" />
                                <p className="text-sm">Mulai percakapan dengan admin</p>
                            </div>
                        )}
                        {messages.map((msg, i) => (
                            <div key={msg.id || i} className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm shadow-soft ${
                                        msg.sender === "user"
                                            ? "rounded-br-none bg-primary-600 text-white"
                                            : "rounded-bl-none border border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                    }`}
                                >
                                    <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <form onSubmit={handleSendMessage} className="flex items-center gap-2 border-t border-slate-100 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
                        <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Ketik pesan..."
                            className="field-base py-2.5"
                        />
                        <button
                            type="submit"
                            disabled={!newMessage.trim()}
                            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="Kirim pesan"
                        >
                            <FaPaperPlane className="h-4 w-4" />
                        </button>
                    </form>
                </div>
            )}

            {/* Floating button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary-600 to-secondary-600 text-white shadow-pop transition-transform hover:scale-105 active:scale-95"
                aria-label={isOpen ? "Tutup chat" : "Buka chat"}
            >
                {isOpen ? <FaTimes size={22} /> : <FaComments size={22} />}
            </button>
        </div>
    );
};

export default ChatWidget;
